import {Component, Input, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {configVariableValues} from '@constants/config-variable-values.constants';
import {TranslateService} from '@ngx-translate/core';
import {AppConfigService} from '@services/app-config.service';
import {LoginService} from '@services/login.service';
import {PermissionService} from '@services/permission.service';
import {ngxUiTourAppTours} from '@shared/constants/ngx-ui-tour.constants';
import {Session} from '@shared/models/session.model';
import {
  DispatcherService,
  VerticalMenuItem
} from 'front-base-angular-core/angular/projects/front-base-angular-core/src/public-api';
import {CircleService} from '@shared/services/circle.service';

@Component({
  selector: 'si-nav-sidebar',
  templateUrl: './nav-sidebar.component.html',
  styleUrls: ['./nav-sidebar.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class NavSidebarComponent implements OnInit, OnDestroy {
  observerKey = 'nav-sidebar';
  items: VerticalMenuItem[];
  userSession: Session;
  ngxUiTourAppTours = ngxUiTourAppTours;
  showTour: boolean;
  clickOutsideInactive: boolean;
  private _collapsed = false;

  constructor(
    private dispatcher: DispatcherService,
    private translateService: TranslateService,
    private loginService: LoginService,
    private permissionService: PermissionService,
    private appConfigService: AppConfigService,
    private circleService: CircleService
  ) {
  }

  get collapsed() {
    return this._collapsed;
  }

  @Input()
  set collapsed(val) {
    this._collapsed = val;
    this.manageBodyOverflowHidden();

    if (!val) {
      this.dispatcher.exec('pasi-nav-click-outside-menu');
      this.checkShowTour();
    }
  }

  ngOnInit() {
    this.userSession = this.loginService.getSession();
    this.setItems();
    this.dispatcher.register('si-nav-sidebar-toggle', () => this.onToggleNavSidebar(), this.observerKey);
  }

  async setItems(): Promise<void> {
    const hasAuthorizedPatients = await this.circleService.getHasAuthorizedPatients();
    const homeText = await this.translateService.get('SI_HOME').toPromise();
    const appointmentText = this.translateService.instant('SI_APPOINTMENTS');
    const interventionText = this.translateService.instant('SI_INTERVENTIONS');
    const medicalTestsText = this.translateService.instant('SI_MEDICAL_REQUESTS');
    const diaryText = this.translateService.instant('SI_DIARY');
    const historyText = this.translateService.instant('SI_CLINICAL_HISTORY');
    const permissionText = this.translateService.instant('SI_PERMISSION_MANAGEMENT');
    const profileText = this.translateService.instant('SI_PROFILE');
    const medicalTeamText = this.translateService.instant('SI_MEDICAL_TEAM');
    const userManagementText = this.translateService.instant('SI_USER_MANAGEMENT');
    const ambulanceText = this.translateService.instant('SI_SCHEDULE_AMBULANCE');
    const bePhonedText = this.translateService.instant('SI_REQUEST_CALL');
    const vitalSignsText = this.translateService.instant('SI_VITAL_SIGNS_SHORT');
    const questionnaireText = this.translateService.instant('SI_QUESTIONNAIRES');
    const medicationText = this.translateService.instant('SI_MEDICATION');
    const pendingTasksText = this.translateService.instant('SI_MY_PENDING_TASKS');
    const attachedDocumentationText = this.translateService.instant('SI_ATTACHED_DOCUMENTATION');
    const myTasksText = this.translateService.instant('SI_MY_TASKS');
    const patientAreaText = this.translateService.instant('SI_PATIENT_AREA');
    const centerServicesText = this.translateService.instant('SI_CENTER_AREA');
    const sendSuggestionText = this.translateService.instant('SI_SEND_SUGGESTION_TITLE');
    const administrationText = this.translateService.instant('SI_ADMINISTRATION');
    const medicalTeamManagementText = this.translateService.instant('SI_MEDICAL_TEAM_MANAGEMENT');
    const circlePatientText = this.translateService.instant('SI_MY_CIRCLE');
    const accountText = this.translateService.instant('SI_ACCOUNT');
    const settingsText = this.translateService.instant('SI_SETTINGS');
    const accessAsText = this.translateService.instant('SI_ACCESS_AS');
    const logOutText = this.translateService.instant('SI_LOGOUT');
    const prescriptionText = this.translateService.instant('SI_PRESCRIPTIONS');

    this.items = [
      {
        label: homeText,
        icon: 'icon-home',
        routerLink: '/',
        anchorId: 'menu.home',
        command: () => this.closeMenu()
      },
      {
        label: pendingTasksText,
        icon: 'icosun-gear_clock',
        routerLink: '/task/pending',
        hide: !this.permissionService.hasPermission('patient_pending_requests:access'),
        command: () => this.closeMenu()
      },
      {
        label: patientAreaText,
        icon: 'icosun-user_data',
        hide: !this.permissionService.hasPermissions(
          [
            'appointment_management:search',
            'patient_requests:management',
            'medication:management',
            'prescription:access',
            'clinical_history:access',
            'attached_documentation:access',
            'vital_sign:management',
            'calendar:diary_management',
            'questionnaire:management',
            'procedure:search'
          ],
          'or'
        ),
        items: [
          {
            label: appointmentText,
            icon: 'icosun-calendar',
            routerLink: '/appointment',
            hide: !this.permissionService.hasPermission('appointment_management:search'),
            anchorId: 'menu.appointment',
            command: () => this.closeMenu()
          },
          {
            label: historyText,
            icon: 'icosun-user_folder',
            routerLink: '/history',
            hide: !this.permissionService.hasPermission('clinical_history:access'),
            anchorId: 'menu.history',
            command: () => this.closeMenu()
          },
          {
            label: medicalTestsText,
            icon: 'icosun-radiography',
            routerLink: '/medical-test',
            hide: !this.permissionService.hasPermission('patient_requests:management'),
            anchorId: 'menu.medical-test',
            command: () => this.closeMenu()
          },
          {
            label: interventionText,
            icon: 'icosun-operating-room',
            routerLink: '/intervention',
            hide: !this.permissionService.hasPermission('intervention:search'),
            anchorId: 'menu.intervention',
            command: () => this.closeMenu()
          },
          {
            label: medicationText,
            icon: 'icosun-pills',
            hide: !this.permissionService.hasPermission('medication:management'),
            routerLink: '/medication',
            command: () => this.closeMenu()
          },
          {
            label: prescriptionText,
            icon: 'icosun-external-medication-box',
            hide: !this.permissionService.hasPermission('prescription:access'),
            routerLink: '/prescription',
            command: () => this.closeMenu()
          },
          {
            label: attachedDocumentationText,
            icon: 'icosun-attached',
            hide: !this.permissionService.hasPermission('attached_documentation:access'),
            routerLink: '/attached-documentation',
            command: () => this.closeMenu()
          },
          {
            label: vitalSignsText,
            icon: 'icosun-heart_pulse',
            hide: !this.permissionService.hasPermission('vital_sign:management')
              || !this.appConfigService.hasConfigurationsEnabled([configVariableValues.VITAL_SIGNS]),
            routerLink: '/vital-sign',
            anchorId: 'menu.vital-sign',
            command: () => this.closeMenu()
          },
          {
            label: diaryText,
            icon: 'icosun-notebook',
            routerLink: '/diary',
            hide: !this.permissionService.hasPermission('calendar:diary_management'),
            anchorId: 'menu.diary',
            command: () => this.closeMenu()
          },
          {
            label: questionnaireText,
            icon: 'icosun-list-box',
            hide: !this.permissionService.hasPermission('questionnaire:management')
              || !this.appConfigService.hasConfigurationsEnabled([configVariableValues.CAREPATH]),
            routerLink: '/questionnaire',
            anchorId: 'menu.questionnaire',
            command: () => this.closeMenu()
          },
          {
            label: myTasksText,
            icon: 'icosun-formalities',
            hide: !this.permissionService.hasPermission('procedure:search'),
            routerLink: '/request/list',
            command: () => this.closeMenu()
          }
        ]
      },
      {
        label: centerServicesText,
        icon: 'icosun-hospital-building',
        hide: !this.permissionService.hasPermissions(
          [
            'his_get:medical_team',
            'procedure:ambulance',
            'procedure:suggestion',
            'procedure:callme'
          ],
          'or'
        ),
        items: [
          {
            label: medicalTeamText,
            icon: 'icosun-administration',
            routerLink: '/medical-team',
            hide: !this.permissionService.hasPermission('his_get:medical_team'),
            anchorId: 'menu.medical-team',
            command: () => this.closeMenu()
          },
          {
            label: ambulanceText,
            icon: 'icosun-ambulance',
            routerLink: '/request/ambulance',
            hide: !this.permissionService.hasPermission('procedure:ambulance'),
            anchorId: 'menu.schedule-ambulance',
            command: () => this.closeMenu()
          },
          {
            label: sendSuggestionText,
            icon: 'icosun-message-reply-text',
            routerLink: '/request/send-suggestion',
            hide: !this.permissionService.hasPermission('procedure:suggestion'),
            command: () => this.closeMenu()
          },
          {
            label: bePhonedText,
            icon: 'icosun-phone_call',
            routerLink: '/request/phoned',
            hide: !this.permissionService.hasPermission('procedure:callme'),
            anchorId: 'menu.be-phoned',
            command: () => this.closeMenu()
          },
        ]
      },
      {
        label: administrationText,
        icon: 'icosun-list3',
        hide: !this.permissionService.hasPermissions(
          [
            'system_configuration:roles_permissions',
            'system_configuration:users_bulk',
            'his_get:back_office_medical_team'
          ],
          'or'
        ),
        items: [
          {
            label: permissionText,
            icon: 'icon-usercheck2',
            routerLink: '/permission',
            anchorId: 'menu.configuration',
            hide: !this.permissionService.hasPermission('system_configuration:roles_permissions'),
            command: () => this.closeMenu()
          },
          {
            label: userManagementText,
            icon: 'icosun-user_data',
            routerLink: '/data-maintenance/user-management',
            hide: !this.permissionService.hasPermission('system_configuration:users_bulk'),
            command: () => this.closeMenu()
          },
          {
            label: medicalTeamManagementText,
            icon: 'icosun-calendar',
            routerLink: '/data-maintenance/medical-team',
            hide: !this.permissionService.hasPermission('his_get:back_office_medical_team'),
            command: () => this.closeMenu()
          },
        ]
      },
      {
        label: accountText,
        icon: 'icosun-accountcircle',
        items: [
          {
            label: profileText,
            icon: 'icosun-dashboard-360-2',
            routerLink: '/profile/dashboard',
            anchorId: 'menu.profile',
            command: () => this.closeMenu()
          },
          {
            label: circlePatientText,
            icon: 'icosun-account-supervisor-circle',
            routerLink: '/profile/my-circle',
            command: () => this.closeMenu()
          },
          {
            label: accessAsText,
            icon: 'icosun-arrow_swap',
            routerLink: '/role-change',
            hide: !hasAuthorizedPatients
              || !this.permissionService.hasPermission('my_circle:get_authorized_by'),
            anchorId: 'menu.access-as',
            command: () => this.closeMenu()
          },
          {
            label: settingsText,
            icon: 'icosun-config1',
            routerLink: '/profile/settings',
            command: () => this.closeMenu()
          },
          {
            label: logOutText,
            icon: 'icosun-exit',
            command: () => this.onClickLogout()
          },
        ]
      }
    ];
  }

  closeMenu(): void {
    this.collapsed = false;
  }

  onToggleNavSidebar(): void {
    this.collapsed = !this.collapsed;
    // Wait until menu is initialized
    setTimeout(() => {
      this.checkShowTour();
    }, 300);
  }

  checkShowTour(): void {
    this.showTour = this.collapsed;
  }

  manageBodyOverflowHidden(): void {
    if (this.collapsed) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
  }

  async onClickLogout(): Promise<void> {
    await this.logout();
  }

  async logout(): Promise<void> {
    await this.loginService.logout();
  }

  onStartTour(): void {
    setTimeout(() => {
      this.items.forEach(item => item.expanded = true);
      this.clickOutsideInactive = true;
    });
  }

  onFinishTour(): void {
    this.clickOutsideInactive = false;
  }

  ngOnDestroy() {
    this.dispatcher.unregisterFunction('si-nav-sidebar-toggle', this.observerKey);
  }
}
